import { useState } from "react";
import { usePainAnalytics, usePainClusters, useRawSignals, useScanLogs } from "@/hooks/use-mock-data";
import type { ScanSourceDetail } from "@/hooks/use-mock-data";
import { BentoCard } from "@/components/strangelove/BentoCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity,
  Layers,
  CheckCircle,
  Zap,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowRight,
  Loader2,
  FlaskConical,
  AlertTriangle,
  Radar,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CLASSIFICATIONS = [
  { value: "", label: "All" },
  { value: "pain_signal", label: "Pain Signal" },
  { value: "builder_intel", label: "Builder Intel" },
  { value: "trend_signal", label: "Trend Signal" },
];

export default function PainAnalyticsPage() {
  const { data: analytics, isLoading: analyticsLoading } = usePainAnalytics();
  const { data: scanLogs } = useScanLogs();
  const [industryFilter, setIndustryFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [signalPage, setSignalPage] = useState(1);
  const [showScanBreakdown, setShowScanBreakdown] = useState(true);
  const { data: clusters } = usePainClusters({ industry: industryFilter || undefined });
  const { data: signals, isLoading: signalsLoading } = useRawSignals({
    industry: industryFilter || undefined,
    classification: classFilter || undefined,
    page: signalPage,
  });
  const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
  const [synthesizingCluster, setSynthesizingCluster] = useState<string | null>(null);
  const [runningSynthesis, setRunningSynthesis] = useState(false);

  const handleSynthesizeCluster = async (clusterId: string) => {
    setSynthesizingCluster(clusterId);
    try {
      await api.post<{ message: string }>("/api/synthesis/run");
      toast.success("Synthesis complete! Check the Big Board for new opportunities.");
    } catch {
      toast.error("Synthesis failed. Try again.");
    } finally {
      setSynthesizingCluster(null);
    }
  };

  const statCards = [
    { label: "Total Signals", value: analytics.totalSignals, icon: Activity, color: "#4B75F2" },
    { label: "Clusters", value: analytics.totalClusters, icon: Layers, color: "#44F2D2" },
    { label: "Qualified", value: analytics.qualifiedClusters, icon: CheckCircle, color: "#EBF216" },
    { label: "Opportunities", value: analytics.synthesizedOpps, icon: Zap, color: "#F24B4B" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-brand text-2xl tracking-wide text-foreground">PAIN ANALYTICS</h1>
        <Button
          onClick={async () => {
            setRunningSynthesis(true);
            try {
              const res = await api.post<{
                message: string;
                clustering: { clustersCreated: number; qualified: number };
                synthesis: { opportunitiesCreated: number; opportunitiesUpdated: number };
              }>("/api/synthesis/run");
              toast.success(res.message);
            } catch {
              toast.error("Synthesis failed. Try again.");
            } finally {
              setRunningSynthesis(false);
            }
          }}
          disabled={runningSynthesis}
          className="bg-mint/10 hover:bg-mint/20 text-mint border border-mint/30 font-semibold text-[10px] uppercase tracking-wider"
        >
          {runningSynthesis ? (
            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Synthesizing...</>
          ) : (
            <><FlaskConical className="w-3 h-3 mr-1" /> Run Synthesis</>
          )}
        </Button>
      </div>

      {/* Last Scan Breakdown */}
      <LastScanBreakdown scanLogs={scanLogs} show={showScanBreakdown} onToggle={() => setShowScanBreakdown(!showScanBreakdown)} />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="glass-card rounded-lg p-4 flex items-center gap-3"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${stat.color}15` }}
            >
              <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
            </div>
            <div>
              <p className="font-mono text-xl text-foreground font-bold">
                {analyticsLoading ? <Skeleton className="h-6 w-12" /> : stat.value}
              </p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Industry Heat Map */}
      <BentoCard title="Industry Heat Map" icon={Activity} span="full" index={0}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {analytics.industryBreakdown
            .sort((a, b) => b.count - a.count)
            .map((item) => {
              const maxCount = Math.max(...analytics.industryBreakdown.map((i) => i.count), 1);
              const intensity = Math.min(item.count / maxCount, 1);
              const isSelected = industryFilter === item.industry;

              return (
                <button
                  key={item.industry}
                  onClick={() => setIndustryFilter(isSelected ? "" : item.industry)}
                  className={cn(
                    "rounded-lg p-3 text-left transition-all border",
                    isSelected
                      ? "border-electric-blue bg-electric-blue/10"
                      : "border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.12)]"
                  )}
                  style={{
                    backgroundColor: isSelected ? undefined : `rgba(75, 117, 242, ${intensity * 0.15})`,
                  }}
                >
                  <p className="text-[11px] text-foreground font-medium truncate">{item.industry}</p>
                  <p className="font-mono text-lg text-electric-blue font-bold">{item.count}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">signals</p>
                </button>
              );
            })}
          {analytics.industryBreakdown.length === 0 && (
            <p className="text-sm text-muted-foreground col-span-full">No signals yet. Run a scan first.</p>
          )}
        </div>
        {industryFilter && (
          <button
            onClick={() => setIndustryFilter("")}
            className="mt-2 text-[11px] text-electric-blue hover:underline"
          >
            Clear filter
          </button>
        )}
      </BentoCard>

      {/* Pain Cluster List */}
      <div className="mt-6">
        <BentoCard title="Pain Clusters" icon={Layers} span="full" index={1}>
          <div className="space-y-1">
            {clusters.data.length === 0 ? (
              <p className="text-sm text-muted-foreground">No clusters yet. Signals need to be clustered via synthesis.</p>
            ) : (
              clusters.data.map((cluster) => (
                <div key={cluster.id} className="border border-[rgba(255,255,255,0.06)] rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-[rgba(255,255,255,0.03)] transition-colors text-left"
                  >
                    {expandedCluster === cluster.id ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{cluster.theme}</span>
                        <StatusBadge status={cluster.status} />
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                        <span>{cluster.industry}</span>
                        <span className="font-mono">{cluster.signalCount} signals</span>
                        <span className="font-mono">{cluster.sourceCount} sources</span>
                      </div>
                    </div>
                  </button>
                  {expandedCluster === cluster.id && (
                    <div className="px-3 pb-3 border-t border-[rgba(255,255,255,0.04)]">
                      {cluster.description && (
                        <p className="text-xs text-muted-foreground mt-2 mb-2">{cluster.description}</p>
                      )}
                      {cluster.representativeQuotes && (cluster.representativeQuotes as string[]).length > 0 && (
                        <div className="space-y-1.5 mb-3">
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Quotes:</span>
                          {(cluster.representativeQuotes as string[]).map((quote, i) => (
                            <p key={i} className="text-xs text-mint/80 font-mono italic pl-2 border-l-2 border-mint/20">
                              "{quote}"
                            </p>
                          ))}
                        </div>
                      )}
                      {(cluster.status === "qualified" || cluster.status === "pending") && (
                        <Button
                          size="sm"
                          onClick={() => handleSynthesizeCluster(cluster.id)}
                          disabled={synthesizingCluster === cluster.id}
                          className="text-[10px] bg-electric-blue hover:bg-electric-blue/90 text-white uppercase tracking-wider"
                        >
                          {synthesizingCluster === cluster.id ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Synthesizing...
                            </>
                          ) : (
                            <>
                              Generate Opportunity <ArrowRight className="w-3 h-3 ml-1" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </BentoCard>
      </div>

      {/* Raw Signal Browser */}
      <div className="mt-6">
        <BentoCard title="Raw Signal Browser" icon={Activity} span="full" index={2}>
          {/* Filters */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {CLASSIFICATIONS.map((cls) => (
              <button
                key={cls.value}
                onClick={() => { setClassFilter(cls.value); setSignalPage(1); }}
                className={cn(
                  "text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-lg border transition-colors",
                  classFilter === cls.value
                    ? "border-electric-blue text-electric-blue bg-electric-blue/10"
                    : "border-[rgba(255,255,255,0.08)] text-muted-foreground hover:border-[rgba(255,255,255,0.15)]"
                )}
              >
                {cls.label}
              </button>
            ))}
          </div>

          {/* Signal table */}
          <div className="space-y-1">
            {signalsLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-lg" />
              ))
            ) : signals.data.length === 0 ? (
              <p className="text-sm text-muted-foreground">No signals found.</p>
            ) : (
              signals.data.map((signal) => (
                <div
                  key={signal.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-foreground truncate">{signal.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                      <span className="bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 rounded">{signal.sourcePlatform}</span>
                      {signal.community && <span>{signal.community}</span>}
                      <span className="font-mono">{signal.engagementScore} engagement</span>
                      {signal.classification && (
                        <span className="uppercase">{signal.classification.replace("_", " ")}</span>
                      )}
                    </div>
                  </div>
                  {signal.url && (
                    <a
                      href={signal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-electric-blue shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {signals.total > 50 && (
            <div className="flex items-center justify-center gap-2 mt-3">
              <button
                onClick={() => setSignalPage((p) => Math.max(1, p - 1))}
                disabled={signalPage <= 1}
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                Previous
              </button>
              <span className="text-xs text-muted-foreground font-mono">
                Page {signalPage} of {Math.ceil(signals.total / 50)}
              </span>
              <button
                onClick={() => setSignalPage((p) => p + 1)}
                disabled={signalPage >= Math.ceil(signals.total / 50)}
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </BentoCard>
      </div>
    </div>
  );
}

// ── Last Scan Breakdown (collapsible) ──

function LastScanBreakdown({
  scanLogs,
  show,
  onToggle,
}: {
  scanLogs: { details?: ScanSourceDetail[] | null; completedAt: string | null; status: string }[];
  show: boolean;
  onToggle: () => void;
}) {
  // Find most recent completed scan with details
  const lastScan = scanLogs.find((l) => l.status === "completed" && l.details && (l.details as ScanSourceDetail[]).length > 0);
  if (!lastScan) return null;

  const details = (lastScan.details || []) as ScanSourceDetail[];
  const scanTime = lastScan.completedAt ? new Date(lastScan.completedAt).toLocaleString() : "Unknown";

  // Sort: most signals first, then errors/timeouts
  const sorted = [...details].sort((a, b) => {
    if (a.signalsStored !== b.signalsStored) return b.signalsStored - a.signalsStored;
    const statusOrder = { success: 0, no_signals: 1, timeout: 2, error: 3, skipped: 4 };
    return (statusOrder[a.status] ?? 5) - (statusOrder[b.status] ?? 5);
  });

  const warnings: string[] = [];
  for (const d of details) {
    if (d.status === "timeout") {
      warnings.push(`${d.sourceName} timed out after ${Math.round(d.fetchDurationMs / 1000)}s`);
    } else if (d.status === "error") {
      warnings.push(`${d.sourceName}: ${d.errorMessage?.slice(0, 60) || "error"}`);
    } else if (d.status === "success" && d.rawItemsFetched > 0 && d.signalsStored === 0) {
      if (d.itemsAfterKeywordFilter === 0) {
        warnings.push(`${d.sourceName}: ${d.rawItemsFetched} fetched but 0 passed keyword filter`);
      } else if (d.itemsAfterDedup === 0) {
        warnings.push(`${d.sourceName}: all ${d.itemsAfterKeywordFilter} signals already seen (dedup)`);
      } else {
        warnings.push(`${d.sourceName}: ${d.itemsAfterDedup} new but all classified as noise`);
      }
    }
  }

  const totalFetched = details.reduce((s, d) => s + d.rawItemsFetched, 0);
  const totalStored = details.reduce((s, d) => s + d.signalsStored, 0);

  return (
    <div className="mb-6 glass-card rounded-lg overflow-hidden border border-[rgba(255,255,255,0.06)]">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Radar className="w-4 h-4 text-electric-blue" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            Last Scan Breakdown
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">{scanTime}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-muted-foreground">
            {totalFetched} fetched → {totalStored} stored
          </span>
          {warnings.length > 0 && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-electric-yellow/10 text-electric-yellow">
              {warnings.length} issue{warnings.length > 1 ? "s" : ""}
            </span>
          )}
          {show ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </button>

      {show && (
        <div className="border-t border-[rgba(255,255,255,0.06)] px-4 pb-4">
          {/* Visual progress bars per source */}
          <div className="mt-3 space-y-1.5">
            {sorted.map((d) => {
              const maxItems = Math.max(...details.map((x) => x.rawItemsFetched), 1);
              const barPct = d.rawItemsFetched > 0 ? Math.max((d.signalsStored / maxItems) * 100, 2) : 0;
              const fetchedPct = d.rawItemsFetched > 0 ? (d.rawItemsFetched / maxItems) * 100 : 0;

              const barColor =
                d.status === "timeout" ? "bg-electric-yellow/40" :
                d.status === "error" ? "bg-red-400/40" :
                d.signalsStored > 0 ? "bg-mint/60" : "bg-[rgba(255,255,255,0.08)]";

              const textColor =
                d.status === "timeout" ? "text-electric-yellow" :
                d.status === "error" ? "text-red-400" :
                d.signalsStored > 0 ? "text-mint" : "text-muted-foreground";

              return (
                <div key={d.sourceName} className="flex items-center gap-3">
                  <span className="text-[11px] font-mono text-foreground w-[180px] truncate shrink-0">
                    {d.sourceName}
                  </span>
                  <div className="flex-1 h-3 rounded-full bg-[rgba(255,255,255,0.04)] overflow-hidden relative">
                    {/* Fetched bar (dim background) */}
                    <div
                      className="absolute inset-y-0 left-0 bg-[rgba(255,255,255,0.06)] rounded-full"
                      style={{ width: `${fetchedPct}%` }}
                    />
                    {/* Stored bar (foreground) */}
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${barColor}`}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-mono w-[140px] text-right shrink-0 ${textColor}`}>
                    {d.status === "timeout" ? "TIMEOUT" :
                     d.status === "error" ? "ERROR" :
                     d.status === "skipped" ? "SKIPPED" :
                     `${d.signalsStored} stored`}
                    {d.rawItemsFetched > 0 && d.status === "success" && (
                      <span className="text-muted-foreground">
                        {" "}({d.rawItemsFetched} → {d.itemsAfterKeywordFilter} filtered → {d.itemsAfterDedup} new)
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="mt-3 p-2.5 rounded-lg bg-electric-yellow/5 border border-electric-yellow/10">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle className="w-3 h-3 text-electric-yellow" />
                <span className="text-[10px] text-electric-yellow uppercase tracking-wider font-semibold">
                  {warnings.length} source{warnings.length > 1 ? "s" : ""} need{warnings.length === 1 ? "s" : ""} attention
                </span>
              </div>
              <ul className="space-y-0.5">
                {warnings.map((w, i) => (
                  <li key={i} className="text-[10px] text-electric-yellow/80 font-mono">• {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { color: string; bg: string }> = {
    pending: { color: "#777777", bg: "rgba(119, 119, 119, 0.12)" },
    qualified: { color: "#44F2D2", bg: "rgba(68, 242, 210, 0.12)" },
    disqualified: { color: "#F24B4B", bg: "rgba(242, 75, 75, 0.12)" },
    synthesized: { color: "#4B75F2", bg: "rgba(75, 117, 242, 0.12)" },
  };
  const c = colors[status] || colors.pending!;

  return (
    <span
      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-brand"
      style={{ color: c.color, backgroundColor: c.bg }}
    >
      {status}
    </span>
  );
}
